import React from 'react';
import { colorSet } from '@components/Provider';
import { makeStyles, Theme } from '@material-ui/core';
import CustomizedTypography from './CustomizedTypography';

export interface LabelProps {
  variant?: 'square' | 'round' | 'outlined';
  startIcon?: React.ReactNode;
  backgroundColor?: keyof typeof colorSet | string;
  textColor?: keyof typeof colorSet | string;
  borderColor?: keyof typeof colorSet | string;
  color?:
    | 'default'
    | 'heavyPrimary'
    | 'heavyGray'
    | 'heavyGreen'
    | 'heavyRed'
    | 'heavyYellow'
    | 'lightPrimary'
    | 'lightGray'
    | 'lightGreen'
    | 'lightRed'
    | 'lightYellow'
    | 'gray700';
}

const useStyles = makeStyles<Theme, LabelProps>((theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    width: 'max-content',
    minHeight: 26,
    minWidth: (props) => (props.startIcon ? 64 : 48),
    padding: (props) => (props.startIcon ? '4px 8px 4px 4px' : '4px 8px'),
    backgroundColor: (props) => {
      if (props.variant !== 'outlined') {
        if (props.backgroundColor) {
          return (
            colorSet[props.backgroundColor as keyof typeof colorSet] ??
            props.backgroundColor
          );
        }

        if (props.color === 'heavyPrimary') return colorSet.primary500;
        if (props.color === 'lightPrimary') return colorSet.primary200;

        if (props.color === 'heavyGray') return colorSet.gray400;
        if (props.color === 'lightGray') return colorSet.gray200;
        if (props.color === 'gray700') return colorSet.gray700;

        if (props.color === 'heavyGreen') return colorSet.successMain;
        if (props.color === 'lightGreen') return colorSet.successLight;

        if (props.color === 'heavyRed') return colorSet.errorMain;
        if (props.color === 'lightRed') return colorSet.errorLight;

        if (props.color === 'heavyYellow') return colorSet.warningMain;
        if (props.color === 'lightYellow') return colorSet.warningLight;
        return colorSet.primary500;
      } else {
        return colorSet.gray000;
      }
    },
    border: (props) => {
      if (props.variant === 'outlined') {
        if (props.color?.includes('Primary'))
          return `1px solid ${colorSet.primary500}`;

        if (props.color?.includes('Gray'))
          return `1px solid ${colorSet.gray400}`;

        if (props.color?.includes('Green'))
          return `1px solid ${colorSet.successMain}`;

        if (props.color?.includes('Red'))
          return `1px solid ${colorSet.errorMain}`;

        if (props.color === 'heavyYellow' || props.color === 'lightYellow')
          return `1px solid ${colorSet.warningMain}`;

        return `1px solid ${colorSet.primary500}`;
      } else if (props.backgroundColor === 'gray000') {
        return `1px solid`;
      }
      return '';
    },
    borderColor: (props) => {
      if (
        props.borderColor &&
        (props.variant === 'outlined' || props.backgroundColor === 'gray000')
      ) {
        return (
          colorSet[props.borderColor as keyof typeof colorSet] ??
          props.borderColor
        );
      }
    },
    borderRadius: (props) => {
      if (props.variant === 'round') return 100;

      return 4;
    }
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    ...theme.typography.caption,
    color: (props) => {
      if (props.textColor)
        return (
          colorSet[props.textColor as keyof typeof colorSet] ?? props.textColor
        );

      if (props.variant !== 'outlined') {
        if (props.color === 'heavyPrimary') return colorSet.gray000;
        if (props.color === 'lightPrimary') return colorSet.primary500;

        if (props.color === 'heavyGray') return colorSet.gray900;
        if (props.color === 'lightGray') return colorSet.gray600;
        if (props.color === 'gray700') return colorSet.gray000;

        if (props.color === 'heavyGreen') return colorSet.gray000;
        if (props.color === 'lightGreen') return colorSet.successMain;

        if (props.color === 'heavyRed') return colorSet.gray000;
        if (props.color === 'lightRed') return colorSet.errorMain;

        if (props.color === 'heavyYellow') return colorSet.gray000;
        if (props.color === 'lightYellow') return colorSet.warningMain;
        return colorSet.gray000;
      } else {
        if (props.color?.includes('Primary')) return colorSet.primary500;

        if (props.color?.includes('Gray')) return colorSet.gray900;

        if (props.color?.includes('Green')) return colorSet.successMain;

        if (props.color?.includes('Red')) return colorSet.errorMain;

        if (props.color?.includes('Yellow')) return colorSet.warningMain;

        return colorSet.primary500;
      }
    },

    fontWeight: 500,
    '& svg': {
      width: 20,
      height: 20,
      marginRight: 4
    }
  }
}));

/**
 * @param variant 라벨의 모양
 * @param startIcon 아이콘
 * @param color 라벨의 색상
 * @param children 라벨의 내용
 * @param backgroundColor 라벨의 배경색 keyof colorSet or string
 * @param textColor 라벨의 글자색 keyof colorSet or string
 * @param borderColor 라벨의 외곽선색 keyof colorSet or string
 */

const CustomizedLabel: React.FC<LabelProps> = (props) => {
  const classes = useStyles({
    ...props
  });

  return (
    <CustomizedTypography className={classes.root}>
      <span className={classes.label}>
        {props.startIcon}
        {props.children}
      </span>
    </CustomizedTypography>
  );
};

export default CustomizedLabel;
